import { memo, useEffect, useMemo, useState, type ReactNode } from "react";
import type { MessageContent } from "@tg/shared";
import { ChatAvatar } from "../../components/chat-avatar.js";
import { internalApi, type InternalDeletedMessageRecord } from "../../lib/internal-api.js";
import { contentToText, useRealtimeStore } from "../../lib/store.js";
import { useUiPrefs } from "../../lib/ui-prefs.js";
import { formatDialogTime, formatExactDateTime } from "../../lib/datetime.js";
import { avatarGradientClass } from "./dialog-list.js";

// Общий поиск по сообщениям во всех чатах (запрос №29). Живые сообщения ищет
// Telegram (store.messageSearch ← chat.searchGlobalMessages), удалённые — наш
// gateway по снимкам из «Удалённых» (только при включённой галочке). Оба
// списка сливаются в одну ленту, новые сверху; удалённые помечены.

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 450;
const SNIPPET_RADIUS = 60;

interface SearchRow {
  key: string;
  chatId: string;
  messageId: string;
  outgoing: boolean;
  content: MessageContent;
  occurredAt: string;
  deletedAt: string | null;
}

// Фрагмент текста вокруг первого совпадения, совпадение подсвечено <mark>.
const renderSnippet = (text: string, query: string): ReactNode => {
  const normalizedText = text.replace(/\s+/g, " ").trim();
  const needle = query.trim().toLowerCase();
  const index = needle.length > 0 ? normalizedText.toLowerCase().indexOf(needle) : -1;
  if (index < 0) {
    return normalizedText.length > SNIPPET_RADIUS * 2
      ? `${normalizedText.slice(0, SNIPPET_RADIUS * 2)}…`
      : normalizedText;
  }
  const start = Math.max(0, index - SNIPPET_RADIUS);
  const end = Math.min(normalizedText.length, index + needle.length + SNIPPET_RADIUS);
  return (
    <>
      {start > 0 ? "…" : ""}
      {normalizedText.slice(start, index)}
      <mark className="msg-search__mark">{normalizedText.slice(index, index + needle.length)}</mark>
      {normalizedText.slice(index + needle.length, end)}
      {end < normalizedText.length ? "…" : ""}
    </>
  );
};

interface MessageSearchResultsProps {
  // Уже обрезанный запрос из строки поиска чатов.
  query: string;
  // Галочка «Включая удалённые сообщения».
  includeDeleted: boolean;
}

export const MessageSearchResults = memo(({ query, includeDeleted }: MessageSearchResultsProps) => {
  const accountId = useRealtimeStore((state) => state.accountId);
  const wsUrl = useRealtimeStore((state) => state.wsUrl);
  const chats = useRealtimeStore((state) => state.chats);
  const messageSearch = useRealtimeStore((state) => state.messageSearch);
  const searchGlobalMessages = useRealtimeStore((state) => state.searchGlobalMessages);
  const loadMoreGlobalMessages = useRealtimeStore((state) => state.loadMoreGlobalMessages);
  const clearMessageSearch = useRealtimeStore((state) => state.clearMessageSearch);
  const requestScrollToMessage = useRealtimeStore((state) => state.requestScrollToMessage);
  const selectChat = useRealtimeStore((state) => state.selectChat);
  const resolveKnownChats = useRealtimeStore((state) => state.resolveKnownChats);
  const timeZone = useUiPrefs((state) => state.timeZone);
  const setRightPanelOpen = useUiPrefs((state) => state.setRightPanelOpen);
  const setRightPanelTab = useUiPrefs((state) => state.setRightPanelTab);

  const active = query.length >= MIN_QUERY_LENGTH;

  // Живые сообщения — с задержкой, чтобы не дёргать Telegram на каждую букву.
  useEffect(() => {
    if (!active) {
      clearMessageSearch();
      return;
    }
    const timer = window.setTimeout(() => searchGlobalMessages(query), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [active, query, searchGlobalMessages, clearMessageSearch]);

  // Удалённые — отдельный HTTP-запрос к gateway, только при галочке.
  const [deleted, setDeleted] = useState<{
    query: string;
    records: InternalDeletedMessageRecord[];
    loading: boolean;
    error: string | null;
  }>({ query: "", records: [], loading: false, error: null });
  useEffect(() => {
    if (!active || !includeDeleted || !accountId) {
      setDeleted({ query: "", records: [], loading: false, error: null });
      return;
    }
    let cancelled = false;
    setDeleted((current) => ({ ...current, query, loading: true, error: null }));
    const timer = window.setTimeout(() => {
      internalApi
        .searchDeletedMessages(accountId, query, wsUrl, 100)
        .then((response) => {
          if (!cancelled) {
            setDeleted({ query, records: response.messages, loading: false, error: null });
          }
        })
        .catch((cause) => {
          if (!cancelled) {
            setDeleted({
              query,
              records: [],
              loading: false,
              error: cause instanceof Error ? cause.message : "Не удалось найти удалённые сообщения."
            });
          }
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [active, includeDeleted, accountId, query, wsUrl]);

  const liveFresh = messageSearch.query === query;
  const deletedFresh = includeDeleted && deleted.query === query;

  const rows = useMemo<SearchRow[]>(() => {
    const list: SearchRow[] = [];
    if (liveFresh) {
      for (const hit of messageSearch.hits) {
        list.push({
          key: `live:${hit.chatId}:${hit.message.messageId}`,
          chatId: hit.chatId,
          messageId: hit.message.messageId,
          outgoing: hit.message.outgoing,
          content: hit.message.content,
          occurredAt: hit.message.occurredAt,
          deletedAt: null
        });
      }
    }
    if (deletedFresh) {
      for (const record of deleted.records) {
        list.push({
          key: `deleted:${record.eventId}`,
          chatId: record.chatId,
          messageId: record.messageId,
          outgoing: record.payload.outgoing,
          content: record.payload.content,
          occurredAt: record.payload.occurredAt,
          deletedAt: record.deletedAt
        });
      }
    }
    list.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
    return list;
  }, [liveFresh, messageSearch.hits, deletedFresh, deleted.records]);

  // Чаты удалённых сообщений могут быть не загружены в SPA — просим сводки.
  // Каждый chatId спрашиваем один раз за жизнь компонента: чат мог быть
  // удалён у аккаунта, и тогда он так и останется неизвестным.
  const [requestedChatIds] = useState(() => new Set<string>());
  useEffect(() => {
    const missing = Array.from(
      new Set(
        rows
          .filter((row) => !chats[row.chatId] && !requestedChatIds.has(row.chatId))
          .map((row) => row.chatId)
      )
    );
    if (missing.length > 0) {
      missing.forEach((chatId) => requestedChatIds.add(chatId));
      resolveKnownChats(missing);
    }
  }, [rows, chats, requestedChatIds, resolveKnownChats]);

  if (!active) {
    return null;
  }

  const loading = (liveFresh && messageSearch.loading) || (deletedFresh && deleted.loading);
  const waitingLive = !liveFresh;
  const errorText = (liveFresh ? messageSearch.error : null) ?? (deletedFresh ? deleted.error : null);

  const openRow = (row: SearchRow): void => {
    if (row.deletedAt) {
      // Удалённого сообщения в ленте нет — открываем чат и его раздел «Удалённые».
      selectChat(row.chatId);
      setRightPanelTab("deleted");
      setRightPanelOpen(true);
      return;
    }
    requestScrollToMessage(row.chatId, row.messageId);
  };

  return (
    <div className="msg-search" role="list" aria-label="Сообщения">
      <div className="msg-search__header">
        Сообщения
        {rows.length > 0 ? <span className="msg-search__count">{rows.length}</span> : null}
      </div>
      {rows.map((row) => {
        const chat = chats[row.chatId];
        const title = chat ? (chat.isSavedMessages ? "Избранное" : chat.title) : "Чат";
        return (
          <button
            type="button"
            key={row.key}
            className={row.deletedAt ? "chat msg-search__row msg-search__row--deleted" : "chat msg-search__row"}
            onClick={() => openRow(row)}
            role="listitem"
            title={
              row.deletedAt
                ? `Удалено ${formatExactDateTime(row.deletedAt, timeZone)} — открыть раздел «Удалённые»`
                : "Перейти к сообщению"
            }
          >
            <ChatAvatar
              title={title}
              avatarUrl={chat?.avatarUrl ?? null}
              className={`avatar ${avatarGradientClass(row.chatId)}`}
              savedMessages={chat?.isSavedMessages ?? false}
            />
            <div className="chat-body">
              <div className="chat-top">
                <div className="name">
                  <span className="name-text">{title}</span>
                  {row.deletedAt ? <span className="msg-search__deleted">Удалено</span> : null}
                </div>
                <div className="chat-meta">
                  <span className="time" title={formatExactDateTime(row.occurredAt, timeZone)}>
                    {formatDialogTime(row.occurredAt, timeZone)}
                  </span>
                </div>
              </div>
              <div className="chat-bottom">
                <div className="msg">
                  {row.outgoing ? <span className="pre">Вы:</span> : null}
                  {row.outgoing ? " " : null}
                  <span dir="auto">{renderSnippet(contentToText(row.content), query)}</span>
                </div>
              </div>
            </div>
          </button>
        );
      })}
      {loading || waitingLive ? (
        <div className="msg-search__status">Ищем в Telegram…</div>
      ) : rows.length === 0 ? (
        <div className="msg-search__status">
          {errorText ?? "Сообщений с таким текстом не найдено."}
        </div>
      ) : errorText ? (
        <div className="msg-search__status">{errorText}</div>
      ) : null}
      {!loading && liveFresh && messageSearch.hasMore ? (
        <button type="button" className="dialog-list__load-more" onClick={() => loadMoreGlobalMessages()}>
          Показать ещё
        </button>
      ) : null}
    </div>
  );
});
MessageSearchResults.displayName = "MessageSearchResults";
